/**
 * Shared Mission types used by both the admin tab and the AI chat mission modal.
 */

// ---------------------------------------------------------------------------
// Enums / Constants
// ---------------------------------------------------------------------------

export const MISSION_PHASES = ['discovery', 'planning', 'execution', 'testing', 'deployment'] as const;
export type MissionPhase = (typeof MISSION_PHASES)[number];

export const PHASE_STATUS_MAP: Record<string, string> = {
  discovery: 'discovery',
  planning: 'planning',
  execution: 'executing',
  testing: 'testing',
  deployment: 'deployed',
};

export type MissionStatusFilter = 'active' | 'completed' | 'all';
export type MissionDetailTab = 'overview' | 'plan' | 'execution' | 'discovery' | 'tests' | 'activity';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface MissionBudget {
  tokens_allocated: number;
  tokens_used: number;
  cost_allocated_usd: number;
  cost_used_usd: number;
  daily_token_limit: number;
}

export interface MissionPlanPhase {
  phase: string;
  description: string;
  estimated_tokens: number;
  tasks: string[];
}

export interface MissionPlan {
  version: number;
  self_critique: string;
  user_approved: boolean;
  approved_at?: string;
  phases: MissionPlanPhase[];
}

export interface MissionArtifact {
  type: string;
  id: string;
  name: string;
  created_at: string;
  phase: string;
  externalId?: string;
  service?: string;
  delegationContext?: {
    request: Record<string, any>;
    response: Record<string, any>;
    delegatedAt: string;
    completedAt?: string;
  };
}

export interface MissionEscalation {
  consecutive_failures: number;
  total_escalations: number;
  last_escalation_at?: string;
  stuck_reason?: string;
}

export interface MissionInstruction {
  instruction_id: string;
  text: string;
  sent_at: string;
  acknowledged_at?: string;
  phase_when_sent: string;
}

export interface MissionMessage {
  message_id: string;
  role: 'user' | 'mission';
  text: string;
  timestamp: string;
  phase: string;
  instruction_id?: string;
  action?: string;
  awaiting_reply?: boolean;
}

export interface MissionMetadata {
  discovery?: {
    findings?: string;
    requirements?: string[];
    existing_resources?: string[];
    gaps?: string[];
    suggested_artifacts?: Array<{ type: string; name: string; description: string }>;
    iteration?: number;
    sufficient?: boolean;
  };
  planning?: {
    approvalChecks?: number;
  };
  execution?: {
    completedTasks?: string[];
    currentTaskIndex?: number;
    artifactSpecs?: Record<string, { type: string; name: string; specification: string; delegationResult?: string; externalId?: string }>;
  };
  testing?: {
    results?: Array<{ artifactId: string; artifactName: string; passed: boolean; notes?: string; issues?: string[] }>;
    currentIndex?: number;
  };
  feedback?: { rating: number; text: string };
  _executing_instance?: string;
  _step_started_at?: string;
}

export interface Mission {
  mission_id: string;
  title: string;
  objective: string;
  origin: string;
  status: string;
  phase: string;
  phase_started_at: string;
  user_id: string;
  agent_id: string;
  parent_mission_id?: string;
  project_id?: string;
  budget: MissionBudget;
  plan?: MissionPlan;
  artifacts: MissionArtifact[];
  escalation: MissionEscalation;
  instructions?: MissionInstruction[];
  conversation?: MissionMessage[];
  step_confirmation?: boolean;
  last_heartbeat_at: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  metadata?: MissionMetadata;
}

export interface MissionLog {
  log_id: string;
  mission_id: string;
  timestamp: string;
  action: string;
  phase: string;
  description: string;
  rationale?: string;
  outcome?: string;
  tokens_used: number;
  cost_usd: number;
  model_name?: string;
  model_tier?: number;
  instance_id?: string;
  error?: string;
  report_category: string;
}

export interface StandupReport {
  mission_id: string;
  title: string;
  phase: string;
  status: string;
  yesterday: string[];
  today: string[];
  blockers: string[];
  budget: {
    tokens_used: number;
    tokens_allocated: number;
    cost_used_usd: number;
    cost_allocated_usd: number;
    percent_used: number;
  };
  formatted: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getPhaseIndex(phase: string): number {
  return MISSION_PHASES.indexOf(phase as MissionPhase);
}

export function isMissionPastPhase(mission: Mission, phase: string): boolean {
  const statusPhaseMap: Record<string, number> = {
    proposed: -1, approved: -1,
    discovery: 0, planning: 1, executing: 2, testing: 3, deployed: 4,
    completed: 5, paused: -2, failed: -2, cancelled: -2,
  };
  const missionPhaseIdx = statusPhaseMap[mission.status] ?? -1;
  const checkIdx = getPhaseIndex(phase);
  if (missionPhaseIdx < 0) {
    return getPhaseIndex(mission.phase) > checkIdx;
  }
  return missionPhaseIdx > checkIdx;
}

export function isMissionAtPhase(mission: Mission, phase: string): boolean {
  const expected = PHASE_STATUS_MAP[phase];
  return mission.status === expected || (mission.status === 'paused' && mission.phase === phase);
}

export function missionStatusColor(status: string): string {
  switch (status) {
    case 'proposed': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'approved': return 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20';
    case 'discovery': return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    case 'planning': return 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20';
    case 'executing': return 'bg-amber-500/10 text-amber-600 border-amber-500/20';
    case 'testing': return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
    case 'deployed': return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'paused': return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    case 'completed': return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'failed': return 'bg-red-500/10 text-red-600 border-red-500/20';
    case 'cancelled': return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    default: return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
  }
}

export function missionBudgetPercent(budget: MissionBudget): number {
  if (budget.tokens_allocated === 0) return 0;
  return Math.min(100, Math.round((budget.tokens_used / budget.tokens_allocated) * 100));
}

export function missionBudgetBarColor(percent: number): string {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 80) return 'bg-yellow-500';
  return 'bg-green-500';
}

export function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
